/**
 * Roles and the permission matrix — `docs/html/admin.html#users` (roles half).
 *
 * **Live against the real API.**
 *
 * The grid is the point of this screen, and two things about it are deliberate:
 *
 *  - Only the `(screen, operation)` pairs `iam.screen_operation` declares legal
 *    get a checkbox. "Delete on the Forecast page" is never offered, because it
 *    is not a thing that can exist.
 *  - Saving sends the **complete** grant set, not a diff. The screen sends what
 *    it displays, so a grant cannot survive because someone forgot to send its
 *    removal — a failure mode that stays invisible until an audit finds it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as rolesApi from '../../api/roles.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { useAuth } from '../../hooks/useAuth.js';
import { pushToast } from '../ui/uiSlice.js';
import { PageLoader, Card, Badge, Field, Modal, ErrorState, EmptyState } from '../../components/common/index.jsx';
import { number } from '../../utils/formatters.js';

const SCOPES = ['none', 'own', 'team', 'territory', 'org', 'all'];

export default function RolesAdminPanel() {
  const { can } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const roles = useQuery({ queryKey: queryKeys.roles.list(), queryFn: rolesApi.listRoles });

  // Select the first role once the list arrives, so the grid is never blank on
  // arrival — the matrix is what people came here for.
  useEffect(() => {
    if (!selectedId && roles.data?.length) setSelectedId(roles.data[0].id);
  }, [roles.data, selectedId]);

  if (roles.isLoading) return <PageLoader label="Loading roles" />;
  if (roles.error) return <ErrorState error={roles.error} onRetry={roles.refetch} />;

  return (
    <div className="roles-layout">
      <aside className="roles-list">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h3 className="card-x-title mb-0">Roles</h3>
          {can('admin.roles', 'create') && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => setCreateOpen(true)}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </button>
          )}
        </div>

        <ul className="role-nav">
          {(roles.data ?? []).map((role) => (
            <li key={role.id}>
              <button
                type="button"
                className={`role-nav-item ${selectedId === role.id ? 'is-active' : ''}`}
                onClick={() => setSelectedId(role.id)}
              >
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span className="fw-600 t-sm">{role.name}</span>
                  {role.is_system && <Badge tone="neutral" title="Shared template — read-only">Template</Badge>}
                </div>
                <div className="t-2xs text-muted">
                  {number(role.assigned_user_count)} users · {number(role.permission_count)} grants · scope {role.default_scope}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="roles-detail">
        {selectedId ? (
          <PermissionMatrix roleId={selectedId} />
        ) : (
          <EmptyState icon="bi-shield" title="Choose a role" message="Select a role to see its permission matrix." />
        )}
      </div>

      <CreateRoleModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        templates={(roles.data ?? []).filter((r) => r.is_system)}
        onCreated={(role) => { setSelectedId(role.id); setCreateOpen(false); }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The grid                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The permission grid for one role.
 *
 * Local edits are held in a draft map keyed `screen|operation`; nothing is sent
 * until Save. That is what makes the "complete set" semantics safe — the draft
 * *is* the full picture, so sending it cannot drop something the user never
 * looked at.
 */
function PermissionMatrix({ roleId }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.roles.permissions(roleId),
    queryFn: () => rolesApi.getRolePermissions(roleId),
  });

  /** `screen|operation` → { effect, data_scope } for everything currently granted. */
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!data) return;
    const next = {};
    for (const screen of data.screens) {
      for (const grant of screen.grants) {
        next[`${screen.key}|${grant.operation}`] = { effect: grant.effect, data_scope: grant.data_scope };
      }
    }
    setDraft(next);
    setDirty(false);
    setResult(null);
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      rolesApi.putRolePermissions(roleId, {
        grants: Object.entries(draft).map(([key, value]) => {
          const [screen_key, operation] = key.split('|');
          return { screen_key, operation, effect: value.effect, data_scope: value.data_scope };
        }),
      }),
    onSuccess: (summary) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.permissions(roleId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      setDirty(false);
      setResult(summary);
      if (summary.warnings?.length === 0) {
        dispatch(pushToast({ tone: 'success', message: 'Permissions saved', detail: `${summary.added} added, ${summary.removed} removed.` }));
      }
    },
    onError: (err) => dispatch(pushToast({ tone: 'danger', message: 'Could not save permissions', detail: err.message })),
  });

  if (isLoading) return <PageLoader label="Loading permissions" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const readOnly = data.role.is_system || !can('admin.roles', 'edit');

  /** Toggle a grant on or off. A new grant defaults to allow at the role's own scope. */
  const toggle = (screenKey, operation) => {
    const key = `${screenKey}|${operation}`;
    setDraft((d) => {
      const next = { ...d };
      if (next[key]) delete next[key];
      else next[key] = { effect: 'allow', data_scope: 'own' };
      return next;
    });
    setDirty(true);
  };

  /** Change an existing grant's scope, or flip it between allow and deny. */
  const update = (screenKey, operation, patch) => {
    const key = `${screenKey}|${operation}`;
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
    setDirty(true);
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h3 className="card-x-title mb-0">{data.role.code}</h3>
          <p className="card-x-sub mb-0">
            {Object.keys(draft).length} grants
            {data.role.is_system && ' · shared template, read-only'}
          </p>
        </div>
        {!readOnly && (
          <button type="button" className="btn btn-save" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        )}
      </div>

      {data.role.is_system && (
        <div className="alert-x alert-info-x mb-3">
          <i className="bi bi-info-circle me-2" aria-hidden="true" />
          System templates are shared across tenants and cannot be edited. Clone this role to make
          a version you can change.
        </div>
      )}

      {result?.warnings?.length > 0 && (
        <div className="alert-x alert-warning-x mb-3">
          <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden="true" />
          <div>
            <div className="fw-600">Saved, with warnings</div>
            <ul className="t-xs mb-0 mt-1 ps-3">
              {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      )}

      <div className="matrix-wrap">
        {data.screens.map((screen) => (
          <details key={screen.key} className="matrix-screen" open={screen.grants.length > 0}>
            <summary>
              <span className="fw-600 t-sm">{screen.label}</span>
              <code className="t-2xs text-muted ms-2">{screen.key}</code>
              <span className="matrix-count">
                {screen.available_operations.filter((op) => draft[`${screen.key}|${op}`]).length}
                {' / '}
                {screen.available_operations.length}
              </span>
            </summary>

            <div className="matrix-ops">
              {screen.available_operations.map((operation) => {
                const key = `${screen.key}|${operation}`;
                const grant = draft[key];
                return (
                  <div key={operation} className={`matrix-op ${grant ? 'is-granted' : ''} ${grant?.effect === 'deny' ? 'is-deny' : ''}`}>
                    <label className="d-flex align-items-center gap-2 flex-grow-1">
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={Boolean(grant)}
                        disabled={readOnly}
                        onChange={() => toggle(screen.key, operation)}
                      />
                      <span className="t-sm">{operation}</span>
                    </label>

                    {grant && (
                      <>
                        <select
                          className="form-select form-select-sm"
                          value={grant.effect}
                          disabled={readOnly}
                          onChange={(e) => update(screen.key, operation, { effect: e.target.value })}
                          aria-label={`${operation} effect`}
                        >
                          <option value="allow">allow</option>
                          <option value="deny">deny</option>
                        </select>
                        <select
                          className="form-select form-select-sm"
                          value={grant.data_scope}
                          disabled={readOnly || grant.effect === 'deny'}
                          onChange={(e) => update(screen.key, operation, { data_scope: e.target.value })}
                          aria-label={`${operation} scope`}
                        >
                          {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </details>
        ))}
      </div>

      <p className="t-2xs text-muted mt-3 mb-0">
        A deny always beats an allow, whichever role it comes from. Data scope resolves the other
        way — the widest scope across a user’s roles applies.
      </p>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Create                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Create a tenant role.
 *
 * Cloning is offered first because building a two-hundred-grant matrix by hand
 * is not something anyone does twice — starting from a template and removing is
 * far quicker than starting empty and adding.
 */
function CreateRoleModal({ open, onClose, templates, onCreated }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [form, setForm] = useState({ code: '', name: '', clone_from_role_id: '', default_scope: 'own' });
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const create = useMutation({
    mutationFn: () =>
      rolesApi.createRole({
        code: form.code,
        name: form.name,
        default_scope: form.default_scope,
        clone_from_role_id: form.clone_from_role_id || undefined,
      }),
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      dispatch(pushToast({ tone: 'success', message: `Created ${role.name}` }));
      setForm({ code: '', name: '', clone_from_role_id: '', default_scope: 'own' });
      onCreated(role);
    },
    onError: (error) => {
      setErrors(error.fieldErrors?.() ?? {});
      if (!error.isValidation) {
        dispatch(pushToast({ tone: 'danger', message: 'Could not create the role', detail: error.message }));
      }
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New role"
      footer={
        <>
          <button type="button" className="btn btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-add"
            disabled={!form.code || !form.name || create.isPending}
            onClick={() => { setErrors({}); create.mutate(); }}
          >
            {create.isPending ? 'Creating…' : 'Create role'}
          </button>
        </>
      }
    >
      <Field label="Code" required error={errors.code} help="Lower-case letters, digits and hyphens. Cannot be changed later.">
        <input className="form-control" value={form.code} onChange={set('code')} placeholder="inside-sales" />
      </Field>
      <Field label="Name" required error={errors.name}>
        <input className="form-control" value={form.name} onChange={set('name')} placeholder="Inside Sales" />
      </Field>
      <Field label="Clone from" help="Copies the whole permission matrix, which you can then trim.">
        <select className="form-select" value={form.clone_from_role_id} onChange={set('clone_from_role_id')}>
          <option value="">Start with no permissions</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Field>
      <Field label="Default scope" help="The data scope new grants take unless overridden.">
        <select className="form-select" value={form.default_scope} onChange={set('default_scope')}>
          {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
    </Modal>
  );
}
